<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import {
		Modal,
		Container,
		Text,
		Button,
		Checkbox,
		Stack,
		Collapse,
		AdvancedCard,
	} from '@Shelly/shelly-design-system';
	import { rooms } from 'src/stores/lists/rooms';
	import { scenes as scenesStore } from 'src/stores/lists/scenes';
	import { extract_actions_variables } from '../../../components/GenericView/extract_actions_variables';
	import { actionInterpretator } from '../../../helpers/scene_actions_to_ui';
	import { getSceneActions } from '../../../helpers/scene_data_helpers';
	import {
		getDelayedSceneAction,
		getSceneActionPurpose,
	} from '../../../helpers/get_delayed_scene_action';
	import type { ISceneObj, SceneWithItems, RoomWithScenes } from 'src/types/SceneType';

	export let showModal = false;
	export let currentSceneId: string | number | undefined;

	const dispatch = createEventDispatcher();

	let roomsWithScenes: RoomWithScenes[] = [];
	let selectedItems: Map<string, number[]> = new Map(); // sceneId -> array of item indices
	let expandedScenes: Map<string | number, boolean> = new Map(); // Track expanded state per scene
	let isInitialized = false;

	$: totalSelectedCount = Array.from(selectedItems.values()).reduce((sum, arr) => sum + arr.length, 0);

	$: if (showModal && !isInitialized) {
		try {
			roomsWithScenes = groupScenesByRoom();
			selectedItems = new Map();
			expandedScenes = new Map();
			roomsWithScenes.forEach((room) => {
				room.scenes.forEach((scene) => {
					expandedScenes.set(scene.scene._id, false);
				});
			});
		} finally {
			isInitialized = true;
		}
	} else if (!showModal) {
		isInitialized = false;
	}

	// Extract actions from a scene
	const extractItemsFromScene = (scene: ISceneObj): SceneWithItems['items'] => {
		const items: SceneWithItems['items'] = [];

		const actions = getSceneActions(scene);
		actions.forEach((action, index) => {
			// Extract action if present in delay wrapper
			const delayedAction = getDelayedSceneAction(action);
			const actualAction = delayedAction !== undefined ? delayedAction : action;

			const purpose = getSceneActionPurpose(action, delayedAction);
			const msgType = actualAction?.msg_type || action?.msg_type;
			const translated = actionInterpretator(purpose, actualAction, msgType);

			if (translated) {
				const displayInfo = extract_actions_variables(translated);
				items.push({
					index,
					data: action,
					translated,
					displayInfo,
					selected: false,
				});
			}
		});

		return items;
	};

	// Group scenes by room
	const groupScenesByRoom = (): RoomWithScenes[] => {
		const grouped: Map<number | string, SceneWithItems[]> = new Map();

		Object.values($scenesStore).forEach((scene: ISceneObj) => {
			// Skip current scene and alarm scenes
			if (
				scene._id === currentSceneId ||
				(scene as any)._gui_function === 'alarm' ||
				(scene as any).tschem
			) {
				return;
			}

			const items = extractItemsFromScene(scene);

			// Only include scenes that have items
			if (items.length > 0) {
				const roomId = scene._meta?.room ?? -1;
				if (!grouped.has(roomId)) {
					grouped.set(roomId, []);
				}
				grouped.get(roomId)!.push({ scene, items });
			}
		});

		// Convert to array and sort
		const result: RoomWithScenes[] = [];
		grouped.forEach((scenesList, roomId) => {
			const roomName =
				roomId === -1
					? $format('general', { default: 'General' })
					: $rooms[roomId]?.name || 'Unknown';
			result.push({
				roomId,
				roomName,
				scenes: scenesList.sort((a, b) => {
					const posA = a.scene._meta?.position ?? 0;
					const posB = b.scene._meta?.position ?? 0;
					return posA - posB;
				}),
			});
		});

		return result.sort((a, b) => {
			if (a.roomId === -1) return -1;
			if (b.roomId === -1) return 1;
			return 0;
		});
	};

	// Toggle item selection
	const toggleItemSelection = (sceneId: string | number, itemIndex: number) => {
		const key = String(sceneId);

		// Get current selection for this scene
		const currentIndices = selectedItems.get(key) || [];
		const indexPos = currentIndices.indexOf(itemIndex);

		// Create new array with updated selection
		let newIndices: number[];
		if (indexPos > -1) {
			// Remove if already selected
			newIndices = currentIndices.filter((i) => i !== itemIndex);
		} else {
			// Add if not selected
			newIndices = [...currentIndices, itemIndex];
		}

		// Update the Map
		if (newIndices.length === 0) {
			selectedItems.delete(key);
		} else {
			selectedItems.set(key, newIndices);
		}
		selectedItems = new Map(selectedItems);

		// Update visual selection
		roomsWithScenes = roomsWithScenes.map((room) => ({
			...room,
			scenes: room.scenes.map((sceneWithItems) => {
				if (String(sceneWithItems.scene._id) === key) {
					return {
						...sceneWithItems,
						items: sceneWithItems.items.map((item) => ({
							...item,
							selected: newIndices.includes(item.index),
						})),
					};
				}
				return sceneWithItems;
			}),
		}));
	};

	// Handle import
	const handleImport = () => {
		const itemsToImport: Array<{ sceneId: string | number; item: any }> = [];

		selectedItems.forEach((indices, sceneId) => {
			const sceneWithItems = roomsWithScenes
				.flatMap((r) => r.scenes)
				.find((s) => String(s.scene._id) === sceneId);

			if (sceneWithItems) {
				indices.forEach((index) => {
					const item = sceneWithItems.items.find((i) => i.index === index);
					if (item) {
						itemsToImport.push({
							sceneId,
							item: item.data,
						});
					}
				});
			}
		});

		dispatch('import', { items: itemsToImport, type: 'action' });
		handleClose();
	};

	const handleClose = () => {
		selectedItems = new Map();
		showModal = false;
		isInitialized = false;
	};

	const toggleExpansion = (sceneId: string | number) => {
		const current = expandedScenes.get(sceneId) ?? true;
		expandedScenes.set(sceneId, !current);
		expandedScenes = new Map(expandedScenes);
	};
</script>

<Modal
	title={$format('import_actions_from_scene', { default: 'Import actions from another scene' })}
	bind:showModal
	on:close={handleClose}
	size="l"
>
	<Container padding="0" slot="content">
		{#if roomsWithScenes.length === 0}
			<div class="text-center p-l">
				<Text styleType="body">
					{$format('no_scenes_with_actions', {
						default: 'No other scenes with actions found',
					})}
				</Text>
			</div>
		{:else}
			<Stack gap="m" class="p-m">
				{#each roomsWithScenes as room (room.roomId)}
					<div>
						<Text element="h2" class="mb-s">{room.roomName}</Text>
						{#each room.scenes as sceneWithItems (sceneWithItems.scene._id)}
							{@const sceneId = sceneWithItems.scene._id}
							{@const itemsCount = sceneWithItems.items.length}
							{@const itemsLabel = $format('action_count', {
								values: { count: itemsCount },
								default: '{count, plural, one {action} other {actions}}',
							})}
							{@const sceneName = sceneWithItems.scene._meta?.name || 'Unnamed Scene'}
							{@const sceneTitle = `${sceneName} (${itemsCount} ${itemsLabel})`}
							{@const isExpanded = expandedScenes.get(sceneId) ?? true}

							<Collapse
								id={`collapse-scene-${sceneId}`}
								title={sceneTitle}
								class="mb-s"
								expanded={isExpanded}
								on:click={() => toggleExpansion(sceneId)}
							>
								<Stack gap="s">
									{#each sceneWithItems.items as item (item.index)}
										<div class="d-flex align-items-start gap-s">
											<Checkbox
												id={`item-checkbox-${sceneId}-${item.index}`}
												checked={item.selected}
												aria-label={`Select ${item.displayInfo.title}`}
												on:click={() => toggleItemSelection(sceneId, item.index)}
											/>
											<div class="flex-grow-1">
												<AdvancedCard
													isCondensed
													avatar={item.displayInfo.avatar}
													title={item.displayInfo.title}
													subtitle={item.displayInfo.subtitle}
													description={item.displayInfo.description}
													isFocusable={false}
												/>
											</div>
										</div>
									{/each}
								</Stack>
							</Collapse>
						{/each}
					</div>
				{/each}
			</Stack>
		{/if}
	</Container>

	<div slot="footer" class="d-flex justify-content-between align-items-center width-full">
		<div class="d-flex gap-s">
			<Button variant="secondary" on:click={handleClose}>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button variant="primary" disabled={totalSelectedCount === 0} on:click={handleImport}>
				{$format('button_import', { default: 'Import' })} ({totalSelectedCount})
			</Button>
		</div>
	</div>
</Modal>
